/**
 * HierarchyBreadcrumb Component
 *
 * Navigation component for hierarchical cost drill-down.
 * Shows current position: "All Usage > [Project] > [Workflow]"
 * Each level is clickable to navigate up the hierarchy.
 *
 * Technical Design Reference: docs/technical/TD-AC-622-hierarchical-cost-breakdown.md (Section 4.3)
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-622 (Epic: Hierarchical Cost Breakdown)
 * - AC-629 (Frontend: HierarchyBreadcrumb component)
 * - AC-632 (Parent: UsageDashboard wiring)
 *
 * Parent Component: UsageDashboard
 * User Access Path: Settings > Usage Dashboard > (drill into project/workflow)
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { BreadcrumbLevel } from '../../types/tokenUsage';

/**
 * Props for the HierarchyBreadcrumb component.
 */
export interface HierarchyBreadcrumbProps {
  /** Current breadcrumb path */
  levels: BreadcrumbLevel[];
  /** Callback when a breadcrumb level is clicked */
  onNavigate: (level: BreadcrumbLevel, index: number) => void;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Determines if a level name indicates a deleted entity.
 * Deleted entities have "(deleted)" suffix in their name.
 */
const isDeletedLevel = (name: string): boolean => {
  return name.includes('(deleted)');
};

/**
 * HierarchyBreadcrumb displays a navigation breadcrumb for hierarchical cost drill-down.
 * Shows the current position in the hierarchy with clickable parent levels.
 *
 * @example
 * // Three-level hierarchy
 * <HierarchyBreadcrumb
 *   levels={[
 *     { type: 'all', id: null, name: 'All Usage' },
 *     { type: 'project', id: 1, name: 'My Project' },
 *     { type: 'orchestration', id: 5, name: 'Test Orch' }
 *   ]}
 *   onNavigate={(level, index) => handleNavigate(level, index)}
 * />
 */
const HierarchyBreadcrumb: React.FC<HierarchyBreadcrumbProps> = ({
  levels,
  onNavigate,
  className = '',
}) => {
  // Don't show breadcrumb at top level (1 or fewer levels)
  if (levels.length <= 1) {
    return null;
  }

  return (
    <nav
      data-testid="hierarchy-breadcrumb"
      className={`flex items-center text-sm ${className}`}
      aria-label="Hierarchy navigation"
    >
      {levels.map((level, index) => {
        const isLast = index === levels.length - 1;
        const isDeleted = isDeletedLevel(level.name);

        return (
          <React.Fragment key={`${level.type}-${level.id ?? 'all'}`}>
            {/* Separator between levels */}
            {index > 0 && (
              <ChevronRight
                className="w-4 h-4 mx-2 text-gray-400 dark:text-gray-500"
                aria-hidden="true"
                role="img"
              />
            )}

            {/* Level content: button for non-last, plain text for last */}
            {isLast ? (
              <span
                className={`font-medium ${
                  isDeleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-warm-white'
                }`}
              >
                {level.name}
              </span>
            ) : (
              <button
                onClick={() => onNavigate(level, index)}
                className="text-primary-600 hover:text-primary-800 hover:underline"
              >
                {level.name}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default HierarchyBreadcrumb;
